// Test cases for annotator skill assessment
// Each test case includes:
// - config: XML configuration for the Cynaps editor
// - task: Sample data to annotate
// - groundTruth: Expected annotation result
// - difficulty: easy | medium | hard
// - points: Points awarded for correct annotation

export interface TestCase {
  id: string;
  specialty: string;
  title: string;
  description: string;
  difficulty: "easy" | "medium" | "hard";
  points: number;
  config: string;
  task: {
    data: Record<string, any>;
    predictions?: Array<{ result: any[] }>;
    annotations?: Array<{ id: number; result: any[] }>;
  };
  groundTruth: AnnotationResult[];
  scoringCriteria: ScoringCriteria;
}

export interface AnnotationResult {
  type: string;
  value: any;
  from_name: string;
  to_name: string;
}

export interface ScoringCriteria {
  type: "exact" | "overlap" | "classification" | "bbox_iou";
  threshold?: number; // For overlap/IoU scoring
  partialCredit?: boolean;
}

// ==================== COMPUTER VISION TEST CASES ====================

export const computerVisionTests: TestCase[] = [
  {
    id: "cv-001",
    specialty: "computer-vision",
    title: "Image Classification",
    description: "Classify the main subject of the image",
    difficulty: "easy",
    points: 10,
    config: `
<View>
  <Image name="image" value="$image" zoom="true"/>
  <View style="padding: 20px; margin-top: 1em; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="What is the main subject of this image?"/>
    <Choices name="classification" toName="image" choice="single" showInLine="true">
      <Choice value="Cat"/>
      <Choice value="Dog"/>
      <Choice value="Bird"/>
      <Choice value="Other Animal"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        image: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3a/Cat03.jpg/1200px-Cat03.jpg",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Cat"] },
        from_name: "classification",
        to_name: "image",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: false,
    },
  },
  {
    id: "cv-002",
    specialty: "computer-vision",
    title: "Object Detection - Bounding Box",
    description: "Draw a bounding box around the cat in the image",
    difficulty: "medium",
    points: 20,
    config: `
<View>
  <Image name="image" value="$image" zoom="true"/>
  <RectangleLabels name="label" toName="image">
    <Label value="Cat" background="#8b5cf6"/>
  </RectangleLabels>
  <View style="padding: 10px; margin-top: 1em; font-size: 14px; color: #6b7280;">
    <Header value="Instructions"/>
    <Text name="instructions" value="Draw a tight bounding box around the cat. The box should fully contain the cat without too much extra space."/>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        image: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3a/Cat03.jpg/1200px-Cat03.jpg",
      },
    },
    groundTruth: [
      {
        type: "rectanglelabels",
        value: {
          x: 10,
          y: 5,
          width: 80,
          height: 90,
          rectanglelabels: ["Cat"],
        },
        from_name: "label",
        to_name: "image",
      },
    ],
    scoringCriteria: {
      type: "bbox_iou",
      threshold: 0.5, // IoU threshold for correct detection
      partialCredit: true,
    },
  },
  {
    id: "cv-003",
    specialty: "computer-vision",
    title: "Multi-class Classification",
    description: "Select all applicable attributes for this image",
    difficulty: "medium",
    points: 15,
    config: `
<View>
  <Image name="image" value="$image" zoom="true"/>
  <View style="padding: 20px; margin-top: 1em; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="Select all attributes that apply to this image"/>
    <Choices name="attributes" toName="image" choice="multiple">
      <Choice value="Contains animal"/>
      <Choice value="Outdoor scene"/>
      <Choice value="Indoor scene"/>
      <Choice value="Natural lighting"/>
      <Choice value="Close-up shot"/>
      <Choice value="Contains text"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        image: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3a/Cat03.jpg/1200px-Cat03.jpg",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Contains animal", "Indoor scene", "Close-up shot"] },
        from_name: "attributes",
        to_name: "image",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: true,
    },
  },
];

// ==================== NLP TEST CASES ====================

export const nlpTests: TestCase[] = [
  {
    id: "nlp-001",
    specialty: "natural-language-processing",
    title: "Sentiment Analysis",
    description: "Classify the sentiment of the given text",
    difficulty: "easy",
    points: 10,
    config: `
<View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; margin-bottom: 1em;">
    <Text name="text" value="$text"/>
  </View>
  <View style="padding: 20px; background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2);">
    <Header value="What is the sentiment of this text?"/>
    <Choices name="sentiment" toName="text" choice="single" showInLine="true">
      <Choice value="Positive"/>
      <Choice value="Negative"/>
      <Choice value="Neutral"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        text: "I absolutely loved this product! It exceeded all my expectations and I would highly recommend it to everyone.",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Positive"] },
        from_name: "sentiment",
        to_name: "text",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: false,
    },
  },
  {
    id: "nlp-002",
    specialty: "natural-language-processing",
    title: "Named Entity Recognition",
    description: "Identify and label named entities in the text",
    difficulty: "medium",
    points: 25,
    config: `
<View>
  <View style="padding: 10px; margin-bottom: 1em; font-size: 14px; color: #6b7280;">
    <Header value="Instructions"/>
    <Text name="instructions" value="Select text spans and label them with the appropriate entity type: PERSON, ORGANIZATION, or LOCATION."/>
  </View>
  <Labels name="ner" toName="text">
    <Label value="PERSON" background="#8b5cf6"/>
    <Label value="ORGANIZATION" background="#3b82f6"/>
    <Label value="LOCATION" background="#22c55e"/>
  </Labels>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Text name="text" value="$text"/>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        text: "Elon Musk, the CEO of Tesla, announced that the company will open a new factory in Austin, Texas next year.",
      },
    },
    groundTruth: [
      {
        type: "labels",
        value: { start: 0, end: 9, text: "Elon Musk", labels: ["PERSON"] },
        from_name: "ner",
        to_name: "text",
      },
      {
        type: "labels",
        value: { start: 22, end: 27, text: "Tesla", labels: ["ORGANIZATION"] },
        from_name: "ner",
        to_name: "text",
      },
      {
        type: "labels",
        value: { start: 80, end: 86, text: "Austin", labels: ["LOCATION"] },
        from_name: "ner",
        to_name: "text",
      },
      {
        type: "labels",
        value: { start: 88, end: 93, text: "Texas", labels: ["LOCATION"] },
        from_name: "ner",
        to_name: "text",
      },
    ],
    scoringCriteria: {
      type: "overlap",
      threshold: 0.8,
      partialCredit: true,
    },
  },
  {
    id: "nlp-003",
    specialty: "natural-language-processing",
    title: "Text Classification - Topic",
    description: "Classify the topic of the article excerpt",
    difficulty: "easy",
    points: 10,
    config: `
<View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; margin-bottom: 1em;">
    <Text name="text" value="$text"/>
  </View>
  <View style="padding: 20px; background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2);">
    <Header value="What is the main topic of this text?"/>
    <Choices name="topic" toName="text" choice="single">
      <Choice value="Technology"/>
      <Choice value="Sports"/>
      <Choice value="Politics"/>
      <Choice value="Entertainment"/>
      <Choice value="Science"/>
      <Choice value="Health"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        text: "Scientists at CERN have discovered a new particle that could help explain the mysteries of dark matter. The research team used the Large Hadron Collider to detect the previously unknown subatomic particle.",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Science"] },
        from_name: "topic",
        to_name: "text",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: false,
    },
  },
];

// ==================== MEDICAL IMAGING TEST CASES ====================

export const medicalImagingTests: TestCase[] = [
  {
    id: "med-001",
    specialty: "medical-imaging",
    title: "Chest X-Ray Classification",
    description: "Classify the chest X-ray finding",
    difficulty: "medium",
    points: 20,
    config: `
<View>
  <Image name="image" value="$image" zoom="true"/>
  <View style="padding: 20px; margin-top: 1em; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="What finding is visible in this chest X-ray?"/>
    <Choices name="finding" toName="image" choice="single">
      <Choice value="Normal"/>
      <Choice value="Pneumonia"/>
      <Choice value="Cardiomegaly"/>
      <Choice value="Pleural Effusion"/>
      <Choice value="Nodule/Mass"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        image: "https://upload.wikimedia.org/wikipedia/commons/e/e0/X-ray_of_normal_chest_%28PA_view%2C_large%29.jpg",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Normal"] },
        from_name: "finding",
        to_name: "image",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: false,
    },
  },
  {
    id: "med-002",
    specialty: "medical-imaging",
    title: "Anatomical Region Identification",
    description: "Identify the anatomical regions in the medical image",
    difficulty: "medium",
    points: 25,
    config: `
<View>
  <Image name="image" value="$image" zoom="true"/>
  <View style="padding: 20px; margin-top: 1em; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="Select all anatomical structures visible:"/>
    <Choices name="regions" toName="image" choice="multiple">
      <Choice value="Lungs"/>
      <Choice value="Heart"/>
      <Choice value="Ribs"/>
      <Choice value="Spine"/>
      <Choice value="Diaphragm"/>
      <Choice value="Clavicles"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        image: "https://upload.wikimedia.org/wikipedia/commons/e/e0/X-ray_of_normal_chest_%28PA_view%2C_large%29.jpg",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Lungs", "Heart", "Ribs", "Spine", "Diaphragm", "Clavicles"] },
        from_name: "regions",
        to_name: "image",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: true,
    },
  },
  {
    id: "med-003",
    specialty: "medical-imaging",
    title: "Image Quality Assessment",
    description: "Assess the quality and positioning of the medical image",
    difficulty: "easy",
    points: 15,
    config: `
<View>
  <Image name="image" value="$image" zoom="true"/>
  <View style="padding: 20px; margin-top: 1em; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="Assess the image quality:"/>
    <Choices name="quality" toName="image" choice="single">
      <Choice value="Good - Diagnostic quality"/>
      <Choice value="Acceptable - Minor issues"/>
      <Choice value="Poor - Repeat recommended"/>
      <Choice value="Unusable"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        image: "https://upload.wikimedia.org/wikipedia/commons/e/e0/X-ray_of_normal_chest_%28PA_view%2C_large%29.jpg",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Good - Diagnostic quality"] },
        from_name: "quality",
        to_name: "image",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: false,
    },
  },

  // ---- CT / PT (PET) Scan Bounding Box Annotation ----
  // Design principles:
  //   • Two <Dicom> canvases rendered side-by-side: CT (anatomical) and PT/PET (metabolic).
  //   • Each canvas has its own independent <RectangleLabels> control so that
  //     annotations on CT and PT are stored separately and can be scored independently.
  //   • A shared <Choices> panel below both canvases captures the lesion characterisation
  //     that applies to the finding visible on both modalities.
  //   • "Consistent bbox across frames" is enforced by the Cynaps <Dicom> tag itself:
  //     the annotator scrolls through slices within each canvas and places the box on
  //     the frame where the lesion is most conspicuous; the editor records the
  //     frame-level reference automatically.
  //   • Ground-truth coordinates are expressed as percentages of image dimensions
  //     (x, y, width, height in 0-100 range) matching the Cynaps serialisation format.
  {
    id: "med-004",
    specialty: "medical-imaging",
    title: "CT/PT Scan – Lesion Bounding Box (Multi-Canvas)",
    description:
      "Draw a tight bounding box around the hypermetabolic lesion on BOTH the CT canvas (left) and the PET canvas (right). Then classify the lesion type below.",
    difficulty: "hard",
    points: 40,
    config: `
<View>
  <!-- ── Header & instructions ── -->
  <View style="padding: 12px 16px; background: rgba(139,92,246,0.08); border: 1px solid rgba(139,92,246,0.25); border-radius: 6px; margin-bottom: 12px;">
    <Header value="CT / PET Scan – Lesion Annotation"/>
    <Text name="task_instructions" value="Scroll through each canvas to find the most conspicuous slice. Draw ONE bounding box per canvas around the lesion. Use the same anatomical region on both modalities. Classify the lesion using the panel below."/>
  </View>

  <!-- ── Dual-canvas row ── -->
  <View style="display: flex; gap: 12px; align-items: flex-start;">

    <!-- CT canvas -->
    <View style="flex: 1; min-width: 0;">
      <View style="padding: 6px 10px; background: rgba(59,130,246,0.1); border: 1px solid rgba(59,130,246,0.3); border-radius: 4px 4px 0 0; display: flex; align-items: center; gap: 6px;">
        <Header value="CT  (Anatomical)" size="5"/>
        <Text name="ct_hint" value="— scroll to navigate slices"/>
      </View>
      <Dicom name="ct_scan" value="$ct_url"/>
      <RectangleLabels name="ct_label" toName="ct_scan" strokeWidth="2">
        <Label value="Lesion" background="#3b82f6"/>
        <Label value="Lymph Node" background="#06b6d4"/>
        <Label value="Mass" background="#8b5cf6"/>
        <Label value="Artifact" background="#f59e0b"/>
      </RectangleLabels>
    </View>

    <!-- PET canvas -->
    <View style="flex: 1; min-width: 0;">
      <View style="padding: 6px 10px; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); border-radius: 4px 4px 0 0; display: flex; align-items: center; gap: 6px;">
        <Header value="PET  (Metabolic)" size="5"/>
        <Text name="pt_hint" value="— scroll to navigate slices"/>
      </View>
      <Dicom name="pt_scan" value="$pt_url"/>
      <RectangleLabels name="pt_label" toName="pt_scan" strokeWidth="2">
        <Label value="Lesion" background="#ef4444"/>
        <Label value="Lymph Node" background="#f97316"/>
        <Label value="Mass" background="#ec4899"/>
        <Label value="Artifact" background="#f59e0b"/>
      </RectangleLabels>
    </View>

  </View>

  <!-- ── Shared classification panel ── -->
  <View style="padding: 16px; margin-top: 12px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; border-radius: 6px;">
    <Header value="Lesion Characterisation"/>

    <View style="margin-top: 10px;">
      <Header value="SUV max estimate (PET uptake)" size="5"/>
      <Choices name="suv_range" toName="pt_scan" choice="single" showInLine="true">
        <Choice value="Low (&lt; 2.5)"/>
        <Choice value="Moderate (2.5 – 5)"/>
        <Choice value="High (5 – 10)"/>
        <Choice value="Very High (&gt; 10)"/>
      </Choices>
    </View>

    <View style="margin-top: 10px;">
      <Header value="CT attenuation" size="5"/>
      <Choices name="ct_attenuation" toName="ct_scan" choice="single" showInLine="true">
        <Choice value="Hypodense"/>
        <Choice value="Isodense"/>
        <Choice value="Hyperdense"/>
        <Choice value="Mixed"/>
      </Choices>
    </View>

    <View style="margin-top: 10px;">
      <Header value="Malignancy suspicion" size="5"/>
      <Choices name="malignancy" toName="ct_scan" choice="single" showInLine="true">
        <Choice value="Benign"/>
        <Choice value="Indeterminate"/>
        <Choice value="Suspicious"/>
        <Choice value="Highly suspicious"/>
      </Choices>
    </View>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        // Public-domain DICOM series hosted on the TCIA / OHIF demo server.
        // CT: thorax series with a visible pulmonary nodule in the right lower lobe.
        // PT: corresponding FDG-PET series showing focal uptake at the same location.
        // Both are single-file multi-frame DICOMs so the Cynaps <Dicom> tag can
        // scroll through frames without a ZIP archive.
        ct_url:
          "https://ohif-dicom-json-example.s3.amazonaws.com/LIDC-IDRI-0001/01-01-2000-30178/3000566.000000-03192/1-001.dcm",
        pt_url:
          "https://ohif-dicom-json-example.s3.amazonaws.com/LIDC-IDRI-0001/01-01-2000-30178/3000566.000000-03192/1-002.dcm",
      },
    },
    // Ground-truth bounding boxes expressed as percentage of image dimensions.
    // The lesion (right lower lobe pulmonary nodule) occupies roughly the
    // central-right quadrant of the axial slice where it is most conspicuous.
    groundTruth: [
      {
        type: "rectanglelabels",
        value: {
          x: 52,
          y: 55,
          width: 14,
          height: 12,
          rotation: 0,
          rectanglelabels: ["Lesion"],
        },
        from_name: "ct_label",
        to_name: "ct_scan",
      },
      {
        type: "rectanglelabels",
        value: {
          x: 50,
          y: 53,
          width: 16,
          height: 14,
          rotation: 0,
          rectanglelabels: ["Lesion"],
        },
        from_name: "pt_label",
        to_name: "pt_scan",
      },
      {
        type: "choices",
        value: { choices: ["High (5 – 10)"] },
        from_name: "suv_range",
        to_name: "pt_scan",
      },
      {
        type: "choices",
        value: { choices: ["Isodense"] },
        from_name: "ct_attenuation",
        to_name: "ct_scan",
      },
      {
        type: "choices",
        value: { choices: ["Suspicious"] },
        from_name: "malignancy",
        to_name: "ct_scan",
      },
    ],
    scoringCriteria: {
      type: "bbox_iou",
      threshold: 0.4, // Slightly relaxed: frame selection varies between annotators
      partialCredit: true,
    },
  },

  {
    id: "med-005",
    specialty: "medical-imaging",
    title: "CT/PT Scan – Multi-Frame Nodule Tracking (Multi-Canvas)",
    description:
      "A pulmonary nodule is visible across multiple CT slices. Draw a bounding box on the CT canvas at the slice where the nodule is largest. Confirm the corresponding PET uptake on the PET canvas. Scroll both canvases to find the correct frame.",
    difficulty: "hard",
    points: 35,
    config: `
<View>
  <!-- ── Header ── -->
  <View style="padding: 12px 16px; background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.25); border-radius: 6px; margin-bottom: 12px;">
    <Header value="Multi-Frame Nodule Tracking – CT / PET"/>
    <Text name="task_instructions2" value="Scroll each canvas independently. On the CT canvas, find the axial slice where the nodule appears largest and draw a tight bounding box. On the PET canvas, navigate to the matching slice and confirm uptake with a bounding box. Both boxes must use the 'Nodule' label."/>
  </View>

  <!-- ── Dual-canvas row ── -->
  <View style="display: flex; gap: 12px; align-items: flex-start;">

    <!-- CT canvas -->
    <View style="flex: 1; min-width: 0;">
      <View style="padding: 6px 10px; background: rgba(59,130,246,0.1); border: 1px solid rgba(59,130,246,0.3); border-radius: 4px 4px 0 0;">
        <Header value="CT  (Anatomical)" size="5"/>
      </View>
      <Dicom name="ct_scan2" value="$ct_url"/>
      <RectangleLabels name="ct_label2" toName="ct_scan2" strokeWidth="2">
        <Label value="Nodule" background="#3b82f6"/>
        <Label value="Ground-Glass Opacity" background="#06b6d4"/>
        <Label value="Consolidation" background="#8b5cf6"/>
        <Label value="Pleural Effusion" background="#64748b"/>
      </RectangleLabels>
    </View>

    <!-- PET canvas -->
    <View style="flex: 1; min-width: 0;">
      <View style="padding: 6px 10px; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); border-radius: 4px 4px 0 0;">
        <Header value="PET  (Metabolic)" size="5"/>
      </View>
      <Dicom name="pt_scan2" value="$pt_url"/>
      <RectangleLabels name="pt_label2" toName="pt_scan2" strokeWidth="2">
        <Label value="Nodule" background="#ef4444"/>
        <Label value="Ground-Glass Opacity" background="#f97316"/>
        <Label value="Consolidation" background="#ec4899"/>
        <Label value="Pleural Effusion" background="#64748b"/>
      </RectangleLabels>
    </View>

  </View>

  <!-- ── Nodule characteristics ── -->
  <View style="padding: 16px; margin-top: 12px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; border-radius: 6px;">
    <Header value="Nodule Characteristics"/>

    <View style="margin-top: 10px;">
      <Header value="Nodule morphology (CT)" size="5"/>
      <Choices name="morphology" toName="ct_scan2" choice="single" showInLine="true">
        <Choice value="Solid"/>
        <Choice value="Part-solid (sub-solid)"/>
        <Choice value="Pure ground-glass"/>
        <Choice value="Calcified"/>
      </Choices>
    </View>

    <View style="margin-top: 10px;">
      <Header value="Nodule margin (CT)" size="5"/>
      <Choices name="margin" toName="ct_scan2" choice="single" showInLine="true">
        <Choice value="Smooth"/>
        <Choice value="Lobulated"/>
        <Choice value="Spiculated"/>
        <Choice value="Irregular"/>
      </Choices>
    </View>

    <View style="margin-top: 10px;">
      <Header value="PET metabolic activity" size="5"/>
      <Choices name="pet_activity" toName="pt_scan2" choice="single" showInLine="true">
        <Choice value="No uptake (SUV &lt; 2.5)"/>
        <Choice value="Mild uptake (SUV 2.5 – 4)"/>
        <Choice value="Moderate uptake (SUV 4 – 7)"/>
        <Choice value="Intense uptake (SUV &gt; 7)"/>
      </Choices>
    </View>

    <View style="margin-top: 10px;">
      <Header value="Lung-RADS category" size="5"/>
      <Choices name="lung_rads" toName="ct_scan2" choice="single" showInLine="true">
        <Choice value="1 – Negative"/>
        <Choice value="2 – Benign appearance"/>
        <Choice value="3 – Probably benign"/>
        <Choice value="4A – Suspicious"/>
        <Choice value="4B – Very suspicious"/>
        <Choice value="4X – Additional features"/>
      </Choices>
    </View>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        ct_url:
          "https://ohif-dicom-json-example.s3.amazonaws.com/LIDC-IDRI-0001/01-01-2000-30178/3000566.000000-03192/1-001.dcm",
        pt_url:
          "https://ohif-dicom-json-example.s3.amazonaws.com/LIDC-IDRI-0001/01-01-2000-30178/3000566.000000-03192/1-002.dcm",
      },
    },
    groundTruth: [
      {
        type: "rectanglelabels",
        value: {
          x: 50,
          y: 54,
          width: 15,
          height: 13,
          rotation: 0,
          rectanglelabels: ["Nodule"],
        },
        from_name: "ct_label2",
        to_name: "ct_scan2",
      },
      {
        type: "rectanglelabels",
        value: {
          x: 49,
          y: 52,
          width: 17,
          height: 15,
          rotation: 0,
          rectanglelabels: ["Nodule"],
        },
        from_name: "pt_label2",
        to_name: "pt_scan2",
      },
      {
        type: "choices",
        value: { choices: ["Solid"] },
        from_name: "morphology",
        to_name: "ct_scan2",
      },
      {
        type: "choices",
        value: { choices: ["Spiculated"] },
        from_name: "margin",
        to_name: "ct_scan2",
      },
      {
        type: "choices",
        value: { choices: ["Moderate uptake (SUV 4 – 7)"] },
        from_name: "pet_activity",
        to_name: "pt_scan2",
      },
      {
        type: "choices",
        value: { choices: ["4A – Suspicious"] },
        from_name: "lung_rads",
        to_name: "ct_scan2",
      },
    ],
    scoringCriteria: {
      type: "bbox_iou",
      threshold: 0.4,
      partialCredit: true,
    },
  },
];

// ==================== AUDIO TEST CASES ====================

export const audioTests: TestCase[] = [
  {
    id: "audio-001",
    specialty: "audiospeech-processing",
    title: "Audio Classification",
    description: "Classify the type of sound in the audio clip",
    difficulty: "easy",
    points: 10,
    config: `
<View>
  <Audio name="audio" value="$audio"/>
  <View style="padding: 20px; margin-top: 1em; background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2);">
    <Header value="What type of sound is this?"/>
    <Choices name="sound_type" toName="audio" choice="single">
      <Choice value="Music"/>
      <Choice value="Speech"/>
      <Choice value="Nature sounds"/>
      <Choice value="Urban noise"/>
      <Choice value="Silence"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        audio:
          "https://upload.wikimedia.org/wikipedia/commons/9/9d/Bach_-_Cello_Suite_no._1_in_G_major,_BWV_1007_-_I._Pr%C3%A9lude.ogg",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Music"] },
        from_name: "sound_type",
        to_name: "audio",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: false,
    },
  },
  {
    id: "audio-002",
    specialty: "audiospeech-processing",
    title: "Audio Quality Rating",
    description: "Rate the quality of the audio recording",
    difficulty: "medium",
    points: 15,
    config: `
<View>
  <Audio name="audio" value="$audio"/>
  <View style="padding: 20px; margin-top: 1em; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="Rate the following aspects of the audio"/>
    <View style="margin-top: 1em;">
      <Header value="Audio Clarity" size="5"/>
      <Rating name="clarity" toName="audio" maxRating="5"/>
    </View>
    <View style="margin-top: 1em;">
      <Header value="Background Noise Level (5 = No noise)" size="5"/>
      <Rating name="noise" toName="audio" maxRating="5"/>
    </View>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        audio:
          "https://upload.wikimedia.org/wikipedia/commons/9/9d/Bach_-_Cello_Suite_no._1_in_G_major,_BWV_1007_-_I._Pr%C3%A9lude.ogg",
      },
    },
    groundTruth: [
      {
        type: "rating",
        value: { rating: 5 },
        from_name: "clarity",
        to_name: "audio",
      },
      {
        type: "rating",
        value: { rating: 5 },
        from_name: "noise",
        to_name: "audio",
      },
    ],
    scoringCriteria: {
      type: "exact",
      partialCredit: true,
    },
  },
];

// ==================== CONVERSATIONAL AI TEST CASES ====================

export const conversationalAITests: TestCase[] = [
  {
    id: "conv-001",
    specialty: "conversational-ai",
    title: "Intent Classification",
    description: "Classify the user's intent in the conversation",
    difficulty: "easy",
    points: 10,
    config: `
<View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; margin-bottom: 1em;">
    <Header value="User Message"/>
    <Text name="message" value="$message"/>
  </View>
  <View style="padding: 20px; background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2);">
    <Header value="What is the user's intent?"/>
    <Choices name="intent" toName="message" choice="single">
      <Choice value="Question"/>
      <Choice value="Complaint"/>
      <Choice value="Request"/>
      <Choice value="Greeting"/>
      <Choice value="Feedback"/>
      <Choice value="Other"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        message:
          "Hi, I'd like to cancel my subscription and get a refund for the remaining months. This service hasn't been working properly for weeks.",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Request"] },
        from_name: "intent",
        to_name: "message",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: false,
    },
  },
  {
    id: "conv-002",
    specialty: "conversational-ai",
    title: "Response Quality Evaluation",
    description: "Evaluate the quality of an AI assistant's response",
    difficulty: "medium",
    points: 20,
    config: `
<View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; margin-bottom: 1em;">
    <Header value="User Question"/>
    <Text name="question" value="$question"/>
  </View>
  <View style="padding: 20px; background: rgba(59, 130, 246, 0.05); border: 1px solid rgba(59, 130, 246, 0.2); margin-bottom: 1em;">
    <Header value="AI Response"/>
    <Text name="response" value="$response"/>
  </View>
  <View style="padding: 20px; background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2);">
    <Header value="Rate the response quality"/>
    <View style="margin-top: 1em;">
      <Header value="Relevance (1-5)" size="5"/>
      <Rating name="relevance" toName="response" maxRating="5"/>
    </View>
    <View style="margin-top: 1em;">
      <Header value="Helpfulness (1-5)" size="5"/>
      <Rating name="helpfulness" toName="response" maxRating="5"/>
    </View>
    <View style="margin-top: 1em;">
      <Header value="Accuracy (1-5)" size="5"/>
      <Rating name="accuracy" toName="response" maxRating="5"/>
    </View>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        question: "What is the capital of France?",
        response:
          "The capital of France is Paris. Paris is located in the north-central part of France and is the country's largest city with a population of over 2 million people in the city proper.",
      },
    },
    groundTruth: [
      {
        type: "rating",
        value: { rating: 5 },
        from_name: "relevance",
        to_name: "response",
      },
      {
        type: "rating",
        value: { rating: 5 },
        from_name: "helpfulness",
        to_name: "response",
      },
      {
        type: "rating",
        value: { rating: 5 },
        from_name: "accuracy",
        to_name: "response",
      },
    ],
    scoringCriteria: {
      type: "exact",
      partialCredit: true,
    },
  },
];

// ==================== GENERATIVE AI TEST CASES ====================

export const generativeAITests: TestCase[] = [
  {
    id: "gen-001",
    specialty: "generative-ai",
    title: "Response Comparison",
    description: "Compare two AI-generated responses and select the better one",
    difficulty: "medium",
    points: 15,
    config: `
<View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; margin-bottom: 1em;">
    <Header value="User Prompt"/>
    <Text name="prompt" value="$prompt"/>
  </View>
  <View style="display: grid; grid-template-columns: 1fr 1fr; gap: 1em; margin-bottom: 1em;">
    <View style="padding: 20px; background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2);">
      <Header value="Response A"/>
      <Text name="response_a" value="$response_a"/>
    </View>
    <View style="padding: 20px; background: rgba(59, 130, 246, 0.05); border: 1px solid rgba(59, 130, 246, 0.2);">
      <Header value="Response B"/>
      <Text name="response_b" value="$response_b"/>
    </View>
  </View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="Which response is better overall?"/>
    <Choices name="preference" toName="prompt" choice="single" showInLine="true">
      <Choice value="Response A is much better"/>
      <Choice value="Response A is slightly better"/>
      <Choice value="About the same"/>
      <Choice value="Response B is slightly better"/>
      <Choice value="Response B is much better"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        prompt: "Explain quantum computing in simple terms.",
        response_a:
          "Quantum computing is like having a super-powered calculator that can explore many possibilities at once. While regular computers use bits (0s and 1s), quantum computers use qubits that can be both 0 and 1 simultaneously, allowing them to solve certain complex problems much faster.",
        response_b:
          "Quantum computing utilizes quantum mechanical phenomena such as superposition and entanglement to perform computational operations on quantum bits or qubits, enabling exponential speedup for specific algorithmic classes including Shor's factoring algorithm.",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Response A is much better"] },
        from_name: "preference",
        to_name: "prompt",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: true,
    },
  },
  {
    id: "gen-002",
    specialty: "generative-ai",
    title: "Content Safety Check",
    description: "Evaluate if the AI response is safe and appropriate",
    difficulty: "easy",
    points: 10,
    config: `
<View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; margin-bottom: 1em;">
    <Header value="AI Generated Response"/>
    <Text name="response" value="$response"/>
  </View>
  <View style="padding: 20px; background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2);">
    <Header value="Is this response safe and appropriate?"/>
    <Choices name="safety" toName="response" choice="single" showInLine="true">
      <Choice value="Safe"/>
      <Choice value="Potentially Harmful"/>
      <Choice value="Harmful"/>
    </Choices>
    <View style="margin-top: 1em;">
      <Header value="Select any issues present (if any)" size="5"/>
      <Choices name="issues" toName="response" choice="multiple">
        <Choice value="Contains misinformation"/>
        <Choice value="Biased content"/>
        <Choice value="Offensive language"/>
        <Choice value="Privacy concerns"/>
        <Choice value="No issues"/>
      </Choices>
    </View>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        response:
          "Here's a simple recipe for chocolate chip cookies: Mix 2 cups flour, 1 cup sugar, 1 cup butter, 2 eggs, 1 tsp vanilla, 1 tsp baking soda, and 2 cups chocolate chips. Bake at 375°F for 10-12 minutes. Enjoy!",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Safe"] },
        from_name: "safety",
        to_name: "response",
      },
      {
        type: "choices",
        value: { choices: ["No issues"] },
        from_name: "issues",
        to_name: "response",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: true,
    },
  },
];

// ==================== RANKING TEST CASES ====================

export const rankingTests: TestCase[] = [
  {
    id: "rank-001",
    specialty: "ranking-and-scoring",
    title: "Search Result Relevance",
    description: "Rate the relevance of search results to a query",
    difficulty: "easy",
    points: 15,
    config: `
<View>
  <View style="padding: 20px; background: rgba(139, 92, 246, 0.1); border: 1px solid rgba(139, 92, 246, 0.3); margin-bottom: 1em;">
    <Header value="Search Query"/>
    <Text name="query" value="$query"/>
  </View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="Search Result"/>
    <Text name="result" value="$result"/>
  </View>
  <View style="padding: 20px; margin-top: 1em; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f;">
    <Header value="How relevant is this result to the query?"/>
    <Choices name="relevance" toName="result" choice="single" showInLine="true">
      <Choice value="Highly Relevant"/>
      <Choice value="Somewhat Relevant"/>
      <Choice value="Marginally Relevant"/>
      <Choice value="Not Relevant"/>
    </Choices>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        query: "best Italian restaurants in New York",
        result:
          "Tony's Pizza - Authentic Italian cuisine in the heart of Manhattan. Award-winning pasta dishes and wood-fired pizzas. Open daily for lunch and dinner.",
      },
    },
    groundTruth: [
      {
        type: "choices",
        value: { choices: ["Highly Relevant"] },
        from_name: "relevance",
        to_name: "result",
      },
    ],
    scoringCriteria: {
      type: "classification",
      partialCredit: true,
    },
  },
  {
    id: "rank-002",
    specialty: "ranking-and-scoring",
    title: "Content Quality Rating",
    description: "Rate the overall quality of user-generated content",
    difficulty: "medium",
    points: 20,
    config: `
<View>
  <View style="padding: 20px; background: rgba(255,255,255,0.02); border: 1px solid #1f1f1f; margin-bottom: 1em;">
    <Header value="Product Review"/>
    <Text name="review" value="$review"/>
  </View>
  <View style="padding: 20px; background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2);">
    <Header value="Rate the quality of this review"/>
    <View style="margin-top: 1em;">
      <Header value="Informativeness (1-5)" size="5"/>
      <Rating name="informativeness" toName="review" maxRating="5"/>
    </View>
    <View style="margin-top: 1em;">
      <Header value="Clarity (1-5)" size="5"/>
      <Rating name="clarity" toName="review" maxRating="5"/>
    </View>
    <View style="margin-top: 1em;">
      <Header value="Usefulness (1-5)" size="5"/>
      <Rating name="usefulness" toName="review" maxRating="5"/>
    </View>
  </View>
</View>
    `.trim(),
    task: {
      data: {
        review:
          "Great product! I've been using this laptop for 3 months now. The battery life is excellent (8+ hours), the screen is crisp, and it handles my video editing software without any issues. Only downside is the fan can be a bit loud under heavy load. Would recommend for creative professionals.",
      },
    },
    groundTruth: [
      {
        type: "rating",
        value: { rating: 5 },
        from_name: "informativeness",
        to_name: "review",
      },
      {
        type: "rating",
        value: { rating: 5 },
        from_name: "clarity",
        to_name: "review",
      },
      {
        type: "rating",
        value: { rating: 5 },
        from_name: "usefulness",
        to_name: "review",
      },
    ],
    scoringCriteria: {
      type: "exact",
      partialCredit: true,
    },
  },
];

// ==================== CT / PT SCAN SUBSET ====================
// Slice of medicalImagingTests that contains only the CT/PT multi-canvas tasks.
// Used by the registry aliases below so that annotators who select the
// "ct-scan-analysis" or "pt-scan-analysis" specialty receive only those tasks.
export const ctPtScanTests: TestCase[] = medicalImagingTests.filter((tc) =>
  ["med-004", "med-005"].includes(tc.id),
);

// ==================== TEST CASES REGISTRY ====================

export const ALL_TEST_CASES: Record<string, TestCase[]> = {
  // Primary keys
  "computer-vision": computerVisionTests,
  "natural-language-processing": nlpTests,
  "audiospeech-processing": audioTests,
  "conversational-ai": conversationalAITests,
  "generative-ai": generativeAITests,
  "ranking-and-scoring": rankingTests,
  "medical-imaging": medicalImagingTests,

  // CT / PT scan – dedicated aliases (serve only the multi-canvas bbox tasks)
  "ct-scan-analysis": ctPtScanTests,
  "pt-scan-analysis": ctPtScanTests,
  "pet-scan-analysis": ctPtScanTests,
  "ct-pt-scan": ctPtScanTests,

  // Other medical imaging aliases
  "chest-x-ray": medicalImagingTests,
  "chest-x-ray-analysis": medicalImagingTests,
  "mri-analysis": medicalImagingTests,

  // Computer vision aliases
  "object-detection": computerVisionTests,
  "image-classification": computerVisionTests,
  "image-segmentation": computerVisionTests,

  // NLP aliases
  "text-classification": nlpTests,
  "named-entity-recognition": nlpTests,
  "sentiment-analysis": nlpTests,

  // Audio aliases
  "speech-transcription": audioTests,
  "speaker-diarization": audioTests,
  "audio-classification": audioTests,

  // Conversational AI aliases
  "intent-recognition": conversationalAITests,
  "dialogue-evaluation": conversationalAITests,

  // Generative AI aliases
  "text-generation-evaluation": generativeAITests,
  "image-generation-evaluation": generativeAITests,

  // Ranking aliases
  "structured-data": rankingTests,

  // Fallback for general tests
  general: computerVisionTests,
};

export const getTestCasesForSpecialties = (specialtyIds: string[]): TestCase[] => {
  const testCases: TestCase[] = [];

  for (const id of specialtyIds) {
    const cases = ALL_TEST_CASES[id];
    if (cases) {
      testCases.push(...cases);
    }
  }

  return testCases;
};

export const getTotalPoints = (testCases: TestCase[]): number => {
  return testCases.reduce((total, tc) => total + tc.points, 0);
};

export const getEstimatedTime = (testCases: TestCase[]): number => {
  // Estimate ~2 min for easy, ~3 min for medium, ~5 min for hard
  const timePerDifficulty = { easy: 2, medium: 3, hard: 5 };
  return testCases.reduce((total, tc) => total + timePerDifficulty[tc.difficulty], 0);
};
